import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { bindActionCreators } from "redux";

import Grid from "@material-ui/core/Grid";

import { InitialTripartita, Session, SessionStatus } from "../../api";
import { api } from "../../client";
import { communicationToLoadableProps } from "../../project-utils";
import { get } from "../../config";
import { getsPromptedForContract, hasToAcceptContract, isInCoaching } from "../../project-utils/permissions";
import {
  acceptProcessContractRequest,
  ApplicationState,
  genCompleteEvaluationRequest,
  genProcessesDeleteRequest,
  genUpdateEvaluationRequest,
  processGetRequest,
  processUsersGetRequest,
  resourcesByProcessIdRequest,
  resourcesCreateRequest,
  selectAccountType,
  selectProcessById,
  selectResourcesByProcessId,
  selectSessionsByProcessId,
  selectTripartitaByProcessId,
  selectUsersByProcessId,
  sessionCompleteRequest,
  sessionScheduleRequest,
  sessionsGetRequest,
  sessionUpdateRequest,
  tripartitaGetByProcessIdRequest,
  tripartitaUpdateFinalRequest,
  tripartitaUpdateInitialAsCoacheeRequest,
  tripartitaUpdateInitialAsCoachRequest,
  tripartitaUpdateIntermediateRequest,
  programsEditRequest,
  programByIdGetRequest,
  selectProcessesByProgramId,
  selectProgramById,
} from "../../redux";

import { PermissionGuard } from "../atoms";
import withToggle, { InjectedToggleProps } from "../hocs/withToggle";
import { Contract, NextTripartita, ProcessBar, ProcessPeople, Sessions, TripartitaTable } from "../molecules";
import FinalEvaluation from "../molecules/FinalEvaluation";
import { NextSessionAppointment, Resources } from "../organisms";

interface ProcessProps {
  programId: number;
  processId: number;
}

const mstp = (state: ApplicationState, { processId, programId }: ProcessProps) => {
  const processesDeleteRequest = genProcessesDeleteRequest(processId);
  const completeEvaluationRequest = genCompleteEvaluationRequest(processId);
  const updateEvaluationRequest = genUpdateEvaluationRequest(processId);

  return {
    acceptProcessContractCommunication: acceptProcessContractRequest.communicationSelector(state),
    processesDeleteCommunication: processesDeleteRequest.communicationSelector(state),
    processGetCommunication: processGetRequest.communicationSelector(state),
    updateEvaluationCommunication: updateEvaluationRequest.communicationSelector(state),
    ccceptEvaluationCommunication: completeEvaluationRequest.communicationSelector(state),
    resourcesGetCommunication: resourcesByProcessIdRequest.communicationSelector(state),
    resourcesPostCommunication: resourcesCreateRequest.communicationSelector(state),
    sessionsGetCommunication: sessionsGetRequest.communicationSelector(state),
    sessionsScheduleCommunication: sessionScheduleRequest.communicationSelector(state),
    sessionsUpdateCommunication: sessionUpdateRequest.communicationSelector(state),
    sessionsCompleteCommunication: sessionCompleteRequest.communicationSelector(state),
    tripartitaGetCommunication: tripartitaGetByProcessIdRequest.communicationSelector(state),
    tripartitaInitialUpdateAsCoacheeCommunication: tripartitaUpdateInitialAsCoacheeRequest.communicationSelector(state),
    tripartitaInitialUpdateAsCoachCommunication: tripartitaUpdateInitialAsCoachRequest.communicationSelector(state),
    tripartitaIntermediateUpdateCommunication: tripartitaUpdateIntermediateRequest.communicationSelector(state),
    tripartitaFinalUpdateCommunication: tripartitaUpdateFinalRequest.communicationSelector(state),
    usersCommunication: processUsersGetRequest.communicationSelector(state),

    process: selectProcessById(processId)(state),
    sessions: selectSessionsByProcessId(processId)(state),
    users: selectUsersByProcessId(processId)(state),
    resources: selectResourcesByProcessId(processId)(state),
    tripartita: selectTripartitaByProcessId(processId)(state),
    accountType: selectAccountType(state),
    processes: selectProcessesByProgramId(programId)(state),
    program: selectProgramById(programId)(state),
  };
};

const mdtp = (dispatch, { processId }: ProcessProps) => {
  const processesDeleteRequest = genProcessesDeleteRequest(processId);
  const completeEvaluationRequest = genCompleteEvaluationRequest(processId);
  const updateEvaluationRequest = genUpdateEvaluationRequest(processId);

  return bindActionCreators(
    {
      acceptProcessContract: acceptProcessContractRequest.actionCreator,
      completeEvaluation: completeEvaluationRequest.actionCreator,
      updateEvaluation: updateEvaluationRequest.actionCreator,
      completeSession: sessionCompleteRequest.actionCreator,
      deleteProcess: processesDeleteRequest.actionCreator,
      getSessions: sessionsGetRequest.actionCreator,
      getProcess: processGetRequest.actionCreator,
      getUsers: processUsersGetRequest.actionCreator,
      getResources: resourcesByProcessIdRequest.actionCreator,
      getTripartita: tripartitaGetByProcessIdRequest.actionCreator,
      scheduleSession: sessionScheduleRequest.actionCreator,
      updateSession: sessionUpdateRequest.actionCreator,
      updateTripartitaInitialAsCoachee: tripartitaUpdateInitialAsCoacheeRequest.actionCreator,
      updateTripartitaInitialAsCoach: tripartitaUpdateInitialAsCoachRequest.actionCreator,
      updateTripartitaIntermediate: tripartitaUpdateIntermediateRequest.actionCreator,
      updateTripartitaFinal: tripartitaUpdateFinalRequest.actionCreator,
      uploadResource: resourcesCreateRequest.actionCreator,
      updateProgram: programsEditRequest.actionCreator,
      getProgram: programByIdGetRequest.actionCreator,
    },
    dispatch
  );
};

type Props = ProcessProps & ReturnType<typeof mstp> & ReturnType<typeof mdtp> & RouteComponentProps<any> & InjectedToggleProps;

const initState = {
  contractHasBeenDismissed: false,
  resources: [],
};

class Process extends React.Component<Props, typeof initState> {
  public state = initState;

  private readonly exportRequest = api.exportSessions(this.props.processId);

  public componentWillMount() {
    this.verifyContractState(this.props);
  }

  public componentDidMount() {
    const { getSessions, getProcess, getUsers, getResources, getTripartita, processId, process, getProgram, programId } = this.props;
    getTripartita(processId);
    getSessions(processId);
    getProcess(processId);
    getResources(processId);
    getProgram(programId);

    get("/program/resources?id=" + programId)
      .then((res) => res.json())
      .then((data) => {
        this.setState({ resources: data });
      });

    if (process && process.student && process.teacher) {
      getUsers(process.student.id, process.teacher.id, programId);
    }
  }

  public componentWillUpdate(nextProps) {
    this.verifyContractState(nextProps);
  }

  public componentDidUpdate(lastProps: Props) {
    const { process: lastProcess, processGetCommunication: lastProcessGetCommunication } = lastProps;
    const { process, getUsers, processGetCommunication, getResources, processId } = this.props;

    if (!lastProcess && process) {
      getUsers(process.student.id, process.teacher.id, process.programId);
      getResources(processId);
    }

    if (
      /* if couldn't be found*/
      (lastProcessGetCommunication.fetching && !processGetCommunication.fetching && !processGetCommunication.success && !process) ||
      /* if has been deleted */
      (lastProcess && !process)
    ) {
      this.props.history.replace(`/dashboard/programs/${this.props.programId}`);
    }

    if (lastProcess && process && !lastProcess.isContractAccepted && process.isContractAccepted) {
      this.quitContractInteraction();
    }
  }

  public render() {
    const tripartitaArea = this.renderTripartitaArea();

    const sessionsArea = this.renderSessionArea();

    const isTripartitaUp = this.isTripartitaUp();

    return (
      <>
        <Grid container alignItems={"stretch"} direction={"column"} spacing={24}>
          <Grid item>{this.renderInteractions()}</Grid>
          {this.renderFinalEvaluationArea()}
          {!this.isProcessDone() && <Grid item>{isTripartitaUp ? this.renderNextTripartita() : this.renderNextSessionAppointment()}</Grid>}
          <Grid item>{this.renderPeople()}</Grid>
          {isTripartitaUp ? (
            <>
              {tripartitaArea}
              {sessionsArea}
            </>
          ) : (
            <>
              {sessionsArea}
              {tripartitaArea}
            </>
          )}
          <Grid item>{this.renderDocumentation()}</Grid>
        </Grid>
        {this.renderContract()}
      </>
    );
  }

  private renderInteractions = () => {
    const {
      processesDeleteCommunication: { fetching },
      deleteProcess,
    } = this.props;

    return <ProcessBar exportRequest={this.exportRequest} deleteFetching={fetching} deleteProcess={deleteProcess} />;
  };

  private renderFinalEvaluationArea = () => {
    const { process, updateEvaluationCommunication } = this.props;

    return this.isProcessDone() && process.finalEvaluation ? (
      <PermissionGuard check={isInCoaching}>
        <Grid item>
          <FinalEvaluation updateCommunication={updateEvaluationCommunication} onUpdate={this.updateEvaluation} onComplete={this.completeEvaluation} {...process.finalEvaluation} />
        </Grid>
      </PermissionGuard>
    ) : null;
  };

  private updateEvaluation = (values) => {
    const { updateEvaluation, process } = this.props;

    updateEvaluation(((process as any).finalEvaluation as any).id, values);
  };

  private completeEvaluation = () => {
    const { completeEvaluation, process } = this.props;

    completeEvaluation(((process as any).finalEvaluation as any).id);
  };

  private renderNextTripartita = () => {
    const { tripartitaInitialUpdateAsCoacheeCommunication, tripartitaInitialUpdateAsCoachCommunication, tripartitaIntermediateUpdateCommunication, tripartitaFinalUpdateCommunication, tripartita } =
      this.props;

    return (
      <NextTripartita
        tripartita={tripartita}
        isInitialTripartitaUp={this.isInitialTripartitaUp()}
        isIntermediateTripartitaUp={this.isIntermediateTripartitaUp()}
        isFinalTripartitaUp={this.isFinalTripartitaUp()}
        onComplete={this.onTripartitaCompletion}
        onEdit={this.onTripartitaEdit}
        initialUpdateCommunication={tripartitaInitialUpdateAsCoachCommunication || tripartitaInitialUpdateAsCoacheeCommunication}
        intermediateUpdateCommunication={tripartitaIntermediateUpdateCommunication}
        finalUpdateCommunication={tripartitaFinalUpdateCommunication}
      />
    );
  };

  private renderTripartitaArea = () => {
    if (this.hasAnyTripartita()) {
      const { tripartita } = this.props;

      return (
        <PermissionGuard check={isInCoaching}>
          <Grid item>
            <TripartitaTable tripartitaPackage={tripartita} />
          </Grid>
        </PermissionGuard>
      );
    }

    return null;
  };

  private renderNextSessionAppointment = () => {
    const { sessions, sessionsScheduleCommunication, sessionsUpdateCommunication, sessionsCompleteCommunication } = this.props;

    return (
      <NextSessionAppointment
        onComplete={this.completeSession}
        onSchedule={this.scheduleSession}
        onUpdate={this.updateSession}
        sessionUpdateCommunication={sessionsUpdateCommunication}
        sessionScheduleCommunication={sessionsScheduleCommunication}
        sessionCompletionCommunication={sessionsCompleteCommunication}
        sessions={sessions}
      />
    );
  };

  private renderPeople = () => {
    const {
      usersCommunication,
      users: { teacher, student },
    } = this.props;
    return <ProcessPeople student={student} teacher={teacher} isCached={!!(student || teacher)} {...communicationToLoadableProps(usersCommunication)} />;
  };

  private renderSessionArea = () => {
    const { sessions, sessionsGetCommunication, programId, processes, program, processId } = this.props;

    return (
      <Grid item>
        <Sessions
          getProgram={this.props.getProgram}
          programUpdate={this.props.updateProgram}
          refreshSessions={() => {
            this.props.getSessions(this.props.processId);
          }}
          programId={programId}
          processId={processId}
          sessions={sessions}
          isCached={!!sessions.length && !!processes}
          processes={processes}
          program={program}
          update={async () => {
            this.props.getSessions(processId);
          }}
          {...communicationToLoadableProps(sessionsGetCommunication)}
        />
      </Grid>
    );
  };

  private renderDocumentation = () => {
    const {
      resources,
      resourcesGetCommunication,
      resourcesPostCommunication,
      // program,
    } = this.props;

    // console.log(program.id, '  this.state.resources ', this.state.resources)
    const programResources = this.state.resources;

    return <Resources onUpload={this.uploadResource} resources={[...resources, ...programResources]} {...communicationToLoadableProps(resourcesGetCommunication)} {...resourcesPostCommunication} />;
  };

  private renderContract = () => {
    const {
      acceptProcessContractCommunication,
      isActive,
      users: { teacher, student },
    } = this.props;

    return <Contract student={student} teacher={teacher} isOpen={isActive} onAccept={this.handleContractAcceptance} onDismiss={this.handleContractDismissal} {...acceptProcessContractCommunication} />;
  };

  private scheduleSession = ({ id, date }) => {
    this.props.scheduleSession(id, { date: new Date(date).getTime() });
  };

  private completeSession = ({ id, goals, achievements }) => {
    this.props.completeSession(id, { goals, achievements });
  };

  private updateSession = ({ id, date }) => {
    this.props.updateSession(id, { date: new Date(date).getTime() });
  };

  private uploadResource = (e) => {
    const { uploadResource, processId } = this.props;
    uploadResource(processId, e.target.files[0]);
  };

  private verifyContractState({ isActive, accountType, process }: Props) {
    if (isActive || this.state.contractHasBeenDismissed) return;

    if (process && getsPromptedForContract(accountType) && !process.isContractAccepted) {
      this.promptToAcceptProcessContract();
    }
  }

  private handleContractAcceptance = () => {
    const { acceptProcessContract, processId } = this.props;

    acceptProcessContract(processId);
  };

  private handleContractDismissal = () => {
    const { accountType } = this.props;

    /*
     theoretically this can't happen since the button to dismiss the contract
     does not even appear for people that have to accept it
     */
    if (!hasToAcceptContract(accountType)) {
      this.setState({ contractHasBeenDismissed: true }, this.quitContractInteraction);
    }
  };

  private quitContractInteraction = () => {
    this.props.deactivate();
  };

  private promptToAcceptProcessContract = () => {
    this.props.activate();
  };

  private hasAnyTripartita = () => {
    const { tripartita } = this.props;
    return tripartita && (tripartita.initial || tripartita.intermediate || tripartita.final);
  };

  private isTripartitaUp = () => {
    return this.isInitialTripartitaUp() || this.isIntermediateTripartitaUp() || this.isFinalTripartitaUp();
  };

  private isInitialTripartitaUp = () => {
    const {
      tripartita: { initial },
    } = this.props;

    return Boolean(initial && !initial.done);
  };

  private isIntermediateTripartitaUp = () => {
    const { tripartita } = this.props;
    return Boolean(tripartita.intermediate && !tripartita.intermediate.done && this.getIntermediateTripartitaBarrier() === this.getCompletedSessions());
  };

  private isFinalTripartitaUp = () => {
    const { sessions, tripartita } = this.props;

    return Boolean(tripartita.final && !tripartita.final.done && sessions.every(this.isSessionDone));
  };

  private getIntermediateTripartitaBarrier = () => {
    return Math.ceil(this.props.sessions.length / 2);
  };

  private getCompletedSessions = () => {
    return this.props.sessions.filter(this.isSessionDone).length;
  };

  private onTripartitaEdit = (values) => {
    const {
      updateTripartitaInitialAsCoachee,
      tripartita: { initial },
    } = this.props;

    updateTripartitaInitialAsCoachee((initial as InitialTripartita).id, values);
  };

  private onTripartitaCompletion = (values) => {
    const {
      updateTripartitaInitialAsCoach,
      updateTripartitaIntermediate,
      updateTripartitaFinal,

      tripartita,
    } = this.props;

    const { initial, intermediate, final } = tripartita as any;

    if (this.isInitialTripartitaUp()) {
      updateTripartitaInitialAsCoach(initial.id, { ...values, done: true });
    } else if (this.isIntermediateTripartitaUp()) {
      updateTripartitaIntermediate(intermediate.id, { ...values, done: true });
    } else {
      updateTripartitaFinal(final.id, { ...values, done: true });
    }
  };

  private isSessionDone = ({ status }: Session) => {
    return status === SessionStatus.Done;
  };

  private isProcessDone = () => {
    const { sessions } = this.props;

    return sessions.every(this.isSessionDone) && !this.isTripartitaUp();
  };
}

export default connect(mstp, mdtp)(withRouter(withToggle(Process)));



// WEBPACK FOOTER //
// ./src/components/environments/Process.tsx